import React, { useState } from "react";
import { Button, Tooltip } from "antd";
import { CloseOutlined, LayoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MessageOutlined, TeamOutlined } from "@ant-design/icons";
import { useChat } from "./ChatContext";
import ConversationList from "./ConversationList";
import ThreadView from "./ThreadView";
import ChatViewPane from "./ChatViewPane";
import PeopleList from "./PeopleList";
import NewChannelModal from "./NewChannelModal";
import InviteMembersModal from "./InviteMembersModal";
import NewDmModal from "./NewDmModal";
import ForwardMessageModal from "./ForwardMessageModal";

const LIST_COLLAPSED_KEY = "FR_CHAT_LIST_COLLAPSED";
const PEOPLE_OPEN_KEY = "FR_CHAT_PEOPLE_OPEN";

function readListCollapsed(): boolean {
  try {
    return localStorage.getItem(LIST_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

// Collapsed by default — the people panel is an extra, opt-in pane, not
// part of the primary list<->thread flow.
function readPeopleOpen(): boolean {
  try {
    return localStorage.getItem(PEOPLE_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The 2-pane chat UI itself (conversation list + thread), shared by both the
 * widget popup and the sidebar dock. `variant="widget"` collapses to
 * single-pane list⇄thread navigation (too narrow for a true split); the
 * sidebar's wider dock keeps a true side-by-side split.
 */
const ChatPanel: React.FC<{ variant: "widget" | "sidebar"; onHeaderMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void }> = ({ variant, onHeaderMouseDown }) => {
  const { mode, setMode, selection, clearSelection, activeView, setActiveView, setOpen } = useChat();
  const singlePane = variant === "widget";
  const [listCollapsed, setListCollapsed] = useState(readListCollapsed);
  const [peopleOpen, setPeopleOpen] = useState(readPeopleOpen);

  const toggleListCollapsed = () => {
    setListCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(LIST_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  const togglePeopleOpen = () => {
    setPeopleOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(PEOPLE_OPEN_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  return (
    <div className={`chat-panel chat-panel--${variant}`}>
      <div className={`chat-panel__header${onHeaderMouseDown ? " chat-panel__header--draggable" : ""}`} onMouseDown={onHeaderMouseDown}>
        <span className="chat-panel__header-title">
          {!singlePane && (
            <Tooltip title={listCollapsed ? "Show channel list" : "Hide channel list"}>
              <Button
                type="text"
                size="small"
                icon={listCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={toggleListCollapsed}
              />
            </Tooltip>
          )}
          <MessageOutlined /> Chat
        </span>
        <span className="chat-panel__header-actions">
          <Tooltip title={peopleOpen ? "Hide people" : "Show people"}>
            <Button
              type="text"
              size="small"
              icon={<TeamOutlined />}
              onClick={togglePeopleOpen}
            />
          </Tooltip>
          <Tooltip title={mode === "widget" ? "Dock to sidebar" : "Undock to widget"}>
            <Button
              type="text"
              size="small"
              icon={<LayoutOutlined />}
              onClick={() => setMode(mode === "widget" ? "sidebar" : "widget")}
            />
          </Tooltip>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
        </span>
      </div>

      <div className="chat-panel__body">
        {singlePane ? (
          peopleOpen ? (
            <PeopleList onClose={togglePeopleOpen} onPersonSelect={togglePeopleOpen} />
          ) : selection ? (
            <ThreadView onBack={clearSelection} />
          ) : activeView ? (
            <ChatViewPane onClose={() => setActiveView(null)} />
          ) : (
            <ConversationList />
          )
        ) : (
          <>
            <div className={`chat-panel__list-col${listCollapsed ? " chat-panel__list-col--collapsed" : ""}`}>
              <ConversationList />
            </div>
            <div className="chat-panel__thread-col">
              {activeView ? <ChatViewPane onClose={() => setActiveView(null)} /> : <ThreadView />}
            </div>
            <div className={`chat-panel__people-col${peopleOpen ? "" : " chat-panel__people-col--collapsed"}`}>
              <PeopleList onClose={togglePeopleOpen} />
            </div>
          </>
        )}
      </div>

      <NewChannelModal />
      <InviteMembersModal />
      <NewDmModal />
      <ForwardMessageModal />
    </div>
  );
};

export default ChatPanel;
